import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'

import type { AWXEntity } from '@ankor-io/common/types/awx'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { DialogActions, DialogBody } from '@/ui-components/Dialog'
import { Fieldset, Legend } from '@/ui-components/Fieldset'
import { Input } from '@/ui-components/Input'
import { Radio, RadioField, RadioGroup } from '@/ui-components/Radio'
import { Select } from '@/ui-components/Select'

export const Step1SelectEntityName = ({
  setIsOpen,
  onNext,
  entryType,
  setEntryType,
  selectedEntity,
  setSelectedEntity,
  newEntityName,
  setNewEntityName,
}: {
  setIsOpen: (value: boolean) => void
  onNext: () => void
  entryType: 'existing' | 'new'
  setEntryType: (value: 'existing' | 'new') => void
  selectedEntity: AWXEntity | null
  setSelectedEntity: (value: AWXEntity) => void
  newEntityName: string
  setNewEntityName: (value: string) => void
}) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId } = useUserContext()

  const [entities, setEntities] = useState<AWXEntity[]>([])

  useEffect(() => {
    const loadEntities = async () => {
      try {
        const resp = await fetch(`/api/pay/org/${activeOrganizationId}/awx/entities`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        })

        if (resp.ok) {
          setEntities(await resp.json())
          return
        }
      } catch (err) {
        console.error('Unaable to fetch from /entities: ', err)
      }
    }

    loadEntities()
  }, [activeOrganizationId, getAccessToken])

  const handleEntryTypeChange = (value: string) => {
    setEntryType(value as 'existing' | 'new')
  }

  const handleSelectEntity = (selectedEntityId: string) => {
    const selectedEntity = entities.find((entity) => entity.id === selectedEntityId)
    if (selectedEntity) {
      setSelectedEntity(selectedEntity)
    }
  }

  return (
    <>
      <DialogBody>
        <Fieldset>
          <Legend>Entity</Legend>
          <RadioGroup
            value={entryType}
            onChange={handleEntryTypeChange}
            className="space-y-6"
          >
            <div>
              <RadioField>
                <label
                  className="flex items-center gap-2 w-max text-sm font-medium cursor-pointer text-gray-900 dark:text-white"
                  onClick={() => setEntryType('existing')}
                >
                  <Radio value="existing" />
                  Select Existing Entity
                </label>
              </RadioField>
              {entryType === 'existing' && (
                <Select
                  value={selectedEntity?.id}
                  onChange={(e) => handleSelectEntity(e.target.value)}
                  className="w-full mt-2"
                  disabled={!entities.length}
                >
                  <option value="">Choose entity</option>
                  {entities.map((e) => (
                    <option
                      key={e.id}
                      value={e.id}
                    >
                      {e.name}
                    </option>
                  ))}
                </Select>
              )}
            </div>
            <RadioField>
              <label
                className="flex items-center gap-2 w-max text-sm font-medium cursor-pointer text-gray-900 dark:text-white"
                onClick={() => setEntryType('new')}
              >
                <Radio value="new" />
                New Entity
              </label>
            </RadioField>
          </RadioGroup>
        </Fieldset>
        {entryType === 'new' && (
          <Input
            placeholder="Entity name"
            className="mt-2"
            value={newEntityName}
            onChange={(e) => setNewEntityName(e.target.value)}
          />
        )}
      </DialogBody>
      <DialogActions>
        <Button
          plain
          onClick={() => setIsOpen(false)}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={onNext}
          className="disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={(entryType === 'existing' && !selectedEntity) || (entryType === 'new' && !newEntityName)}
          data-testid="step1-next-button"
        >
          Next
        </Button>
      </DialogActions>
    </>
  )
}
